import Alpine from "alpinejs";
import { updateMobilePdpView } from "./pdp-utils";
import updateMediaGrid from "./pdp-media";
import { fusionAtcAdd } from "./pdp-atc";
import "../../components/custom-yotpo-review-carousel";
import "./pdp-sticky-atc";
import { updateWishlistAndDispatchEvent } from "./wishlistHelper";
import { setFormEventListeners } from "@/entrypoints/utils/ajaxCartHelpers";

export const pdpCore = () => {
  setInitialVariant();
  initComplementaryProductForms();

  // Args e = click event data from clicked on product
  document.addEventListener("newSelectedProduct", (e) => {
    // Do nothing if its already selected
    if (
      e.target.id == Alpine.store("activeProduct").activeProduct.id ||
      !e.target.id ||
      typeof e.target.id == "undefined"
    )
      return;

    // IMPORTANT check for product swapping and call sentry critical errors if issues
    const newProductObject = window.fusionDataProvider.find(
      (o) => o.product.id === parseInt(e.target.id),
    );
    if (!newProductObject || typeof newProductObject == "undefined") return;

    // Capture the previously selected size (option1) and style (option2) BEFORE
    // swapping product, so they can persist across color changes (size even when
    // OOS; style only when the new color offers the same size+style combo).
    const previousProduct = Alpine.store("activeProduct").activeProduct;
    const isPreviousMultiOption =
      previousProduct?.product_option_titles?.length > 1;
    const prevSize = isPreviousMultiOption
      ? ensureVMultiSelectStore().activeOptionOne
      : getSelectedSingleOptionSize(previousProduct?.variants || []);
    const prevStyle = isPreviousMultiOption
      ? ensureVMultiSelectStore().activeOptionTwo
      : "";

    // __+|+__ ALPINE UPDATE __+|+__
    Alpine.store("activeProduct").updateActiveProduct(newProductObject.product);
    updateUrl(newProductObject.product.handle);

    const newProduct = newProductObject.product;
    const sizeExistsInNewColor =
      prevSize && newProduct.variants.some((v) => v.option1 === prevSize);

    // Style never persists across colors; clear its active classes.
    clearMultiOptionActiveClasses();

    if (newProduct.variants.length === 1) {
      const onlyVariant = newProduct.variants[0];
      Alpine.store("selectedVariant").updateActiveVariant(onlyVariant);
      // Keep vMultiSelect in sync with the only variant so stale size/style from
      // a previous color can't leak into a later swap (preserveSizeSelection).
      const store = ensureVMultiSelectStore();
      store.updateActiveOptionOne(onlyVariant.option1 || "");
      store.updateActiveOptionTwo(onlyVariant.option2 || "");
    } else if (sizeExistsInNewColor) {
      preserveSizeSelection(prevSize, prevStyle, newProduct);
    } else {
      // Previous size isn't offered in this color (or nothing was selected) —
      // fall back to a clean "Select A Size" state.
      resetMultiOptionSelection();
      Alpine.store("selectedVariant").updateActiveVariant({
        id: "",
        title: "Select A Size",
        displayAtc: false,
        available: false,
      });
    }

    // update all the media elements that are in the desktop grid.
    updateMediaGrid(newProductObject.product.images, newProductObject);

    updateComplementaryProducts(newProductObject.product.handle);

    // Reset the mobile media slider to its first image.
    updateMobilePdpView();

    scheduleActiveProductAvailabilityRefresh();
  });

  // Args e = click event data from clicked size component
  document.addEventListener("selectedVariant", (e) => {
    // Do nothing if its already selected
    if (e.target.id === Alpine.store("selectedVariant").id) return;

    if (
      Alpine.store("activeProduct")?.activeProduct.product_option_titles
        .length > 1
    ) {
      const store = ensureVMultiSelectStore();
      const dataset = e.target.dataset;
      const selectedItem = dataset.item;
      const isActiveClass = `vselect_option--active-${dataset.parent}`;
      const isSizeClick =
        dataset.optionPosition === "1" || dataset.parent === "size";

      // Update selected item based on parent using Alpine store
      if (isSizeClick) {
        store.updateActiveOptionOne(selectedItem);

        // Clear style selection when size changes (BUF-358 criteria #4)
        store.updateActiveOptionTwo("");
        const styleActiveClass = document.querySelector(
          "[class*='vselect_option--active-']:not([class*='vselect_option--active-size'])",
        );
        if (styleActiveClass) {
          // Extract the style-specific active class
          const classes = [...styleActiveClass.classList];
          const activeStyleClass = classes.find(
            (c) =>
              c.startsWith("vselect_option--active-") &&
              c !== "vselect_option--active-size",
          );
          if (activeStyleClass) {
            styleActiveClass.classList.remove(
              "vselect_option--active",
              activeStyleClass,
            );
          }
        }
      } else {
        store.updateActiveOptionTwo(selectedItem);
      }

      // Toggle active class for the clicked option
      let elm = document.querySelector(`.${isActiveClass}`);
      if (elm) {
        elm.classList.remove("vselect_option--active", isActiveClass);
      }
      e.target.classList.add("vselect_option--active", isActiveClass);

      // Refresh availability
      const variants = Alpine.store("activeProduct").activeProduct.variants;
      refreshSizeAvailability(variants);
      refreshStyleAvailabilityForSize(variants, store.activeOptionOne);
      refreshColorAvailabilityForSize(store.activeOptionOne);

      // Update active variant if both options are selected
      const optionOne = store.activeOptionOne;
      const optionTwo = store.activeOptionTwo;

      if (optionOne && optionTwo) {
        const variant = Alpine.store(
          "activeProduct",
        ).activeProduct.variants.find(
          (o) => `${o.option1}-${o.option2}` === `${optionOne}-${optionTwo}`,
        );

        if (variant) {
          applyFullVariantSelection(variant);
        } else {
          Alpine.store("selectedVariant").updateActiveVariant({
            id: "",
            title: `${getOptionValueLabel(1, optionOne)} - ${getOptionValueLabel(2, optionTwo)} SOLD OUT`,
            displayAtc: false,
            available: false,
          });
        }
      } else if (isSizeClick) {
        applySizeOnlySelection(optionOne);
      }
    } else {
      let elm = document.querySelector(".vselect_option--active");
      if (elm && typeof elm !== "undefined") {
        elm.classList.remove("vselect_option--active");
        e.target.classList.add("vselect_option--active");
      } else {
        e.target.classList.add("vselect_option--active");
      }

      Alpine.store("selectedVariant").updateActiveVariant({
        id: e.target.id,
        title: e.target.innerText,
        displayAtc: true,
        available: e.target.dataset.unavailable ? false : true,
      });
      refreshColorAvailabilityForSize(e.target.dataset.item);

      // Call the handleVariantUpdate function with the new variant
      if (
        Alpine.store("activeProduct") &&
        Alpine.store("activeProduct").activeProduct
      ) {
        handleVariantUpdate(
          Alpine.store("activeProduct").activeProduct.id,
          e.target.id,
          Alpine.store("activeProduct").activeProduct.handle,
        );
      }
    }
  });

  document.addEventListener("pdpAtc", (event) => {
    fusionAtcAdd(event);
  });

  document.addEventListener(
    "initMultiOptionVariantSelector",
    () => {
      refreshActiveProductAvailability();
    },
  );
};

const initComplementaryProductForms = () => {
  const wrapper = document.getElementById("complementary-products-wrapper");

  if (!wrapper) {
    return;
  }

  setFormEventListeners(wrapper);
};

/**
 * Update URL to reflect new product handle.
 * @param {String} handle - Shopify product handle
 * @param {Number} variantId - Variant id (optional)
 */
function updateUrl(handle, variantId) {
  const pathnameArray = window.location.pathname.split("/");
  pathnameArray.splice(pathnameArray.length - 1, 1, handle);
  const pathname = pathnameArray.join("/");

  if (!variantId) {
    window.history.replaceState({}, "", pathname);
    return;
  }

  const paramsObj = { variant: variantId };
  const searchParams = new URLSearchParams(paramsObj);

  window.history.replaceState({}, "", `${pathname}?${searchParams.toString()}`);
}

/**
 * Handle variant update by changing URL variant param and updating wishlist button.
 * @param {Number} productId - Shopify product ID
 * @param {Number} variantId - Shopify variant ID
 * @param {String} handle - Shopify product handle
 */
function handleVariantUpdate(productId, variantId, handle) {
  updateUrl(handle, variantId);

  updateWishlistAndDispatchEvent(productId, variantId, handle);
}

/**
 * When PDP is loaded with variant param in URL, sets variant on page load.
 */
function setInitialVariant() {
  const variantId = new URLSearchParams(window.location.search).get("variant");

  if (!variantId) {
    return;
  }

  const variant = Alpine.store("activeProduct").activeProduct.variants.find(
    (variant) => variant.id == Number(variantId),
  );

  if (!variant) {
    return;
  }

  Alpine.store("selectedVariant").updateActiveVariant(variant);

  if (
    Alpine.store("activeProduct")?.activeProduct.product_option_titles.length >
    1
  ) {
    const store = ensureVMultiSelectStore();
    store.updateActiveOptionOne(variant.option1 || "");
    store.updateActiveOptionTwo(variant.option2 || "");
  }
}

const resetMultiOptionSelection = () => {
  const store = ensureVMultiSelectStore();

  store.updateActiveOptionOne("");
  store.updateActiveOptionTwo("");
};

/**
 * Apply a "size selected, no style yet" state for a multi-option product.
 * Shared by the size-click handler and the color-change handler so the
 * "Select A Style" / "{SIZE} SOLD OUT" logic lives in one place.
 * @param {String} size - Selected option1 value
 */
const applySizeOnlySelection = (size) => {
  const activeProduct = Alpine.store("activeProduct").activeProduct;
  const variants = activeProduct.variants;

  const sizeHasAvailableVariant = variants.some(
    (variant) => variant.option1 === size && variant.available,
  );

  if (!sizeHasAvailableVariant) {
    Alpine.store("selectedVariant").updateActiveVariant({
      id: "",
      title: `${getOptionValueLabel(1, size)} SOLD OUT`,
      displayAtc: true,
      available: false,
    });
    updateUrl(activeProduct.handle);
    return;
  }

  // Size selected but no style yet - prompt user
  Alpine.store("selectedVariant").updateActiveVariant({
    id: "",
    title: "Select A Style",
    displayAtc: false,
  });
  // Clear variant from URL since style was deselected
  updateUrl(activeProduct.handle);
};

/**
 * Select a fully resolved size+style variant (available or sold-out).
 * Shared by the option-click handler and the color-change handler.
 * @param {Object} variant - Variant matched by option1 + option2
 */
const applyFullVariantSelection = (variant) => {
  const activeProduct = Alpine.store("activeProduct").activeProduct;

  Alpine.store("selectedVariant").updateActiveVariant({
    id: variant.id,
    title: getVariantDisplayTitle(variant),
    displayAtc: true,
    available: variant.available,
  });

  handleVariantUpdate(activeProduct.id, variant.id, activeProduct.handle);
};

/**
 * Re-apply a previously selected size to a newly selected color product,
 * keeping the size highlighted across color changes (even when OOS).
 * Style (option2) is preserved too, but only when the new color offers the
 * exact same size+style variant; otherwise style resets.
 * @param {String} size - Previously selected option1 value (exists in product)
 * @param {String} style - Previously selected option2 value (may not exist)
 * @param {Object} product - Newly selected color product
 */
const preserveSizeSelection = (size, style, product) => {
  if (product.product_option_titles?.length > 1) {
    const store = ensureVMultiSelectStore();
    store.updateActiveOptionOne(size);

    // Preserve style only if the new color has this exact size+style variant.
    const comboVariant = style
      ? product.variants.find(
          (v) => v.option1 === size && v.option2 === style,
        )
      : null;

    if (comboVariant) {
      store.updateActiveOptionTwo(style);
      applyFullVariantSelection(comboVariant);
      return;
    }

    store.updateActiveOptionTwo(""); // style not offered here — reset it
    applySizeOnlySelection(size);
    return;
  }

  // Single-axis (size only): re-select the matching variant in the new color,
  // even if it's sold out (showBisTrigger handles the back-in-stock prompt).
  const variant = product.variants.find((v) => v.option1 === size);
  if (!variant) return;

  Alpine.store("selectedVariant").updateActiveVariant({
    id: variant.id,
    title: getVariantDisplayTitle(variant),
    displayAtc: true,
    available: variant.available,
  });
  handleVariantUpdate(product.id, variant.id, product.handle);
};

const clearMultiOptionActiveClasses = () => {
  document
    .querySelectorAll(".fusion-pdp__vselect-multi .vselect_option--active")
    .forEach((option) => {
      option.classList.remove("vselect_option--active");

      [...option.classList]
        .filter((className) =>
          className.startsWith("vselect_option--active-"),
        )
        .forEach((className) => option.classList.remove(className));
    });
};

const getVariantDisplayTitle = (variant) => {
  return variant.title?.replace(" / ", " - ") || "";
};

const getOptionValueLabel = (position, value) => {
  const activeProduct = Alpine?.store("activeProduct")?.activeProduct;
  const variant = activeProduct?.variants?.find(
    (item) => item[`option${position}`] === value,
  );

  return variant?.[`option${position}_label`] || value;
};

const refreshActiveProductAvailability = () => {
  const activeProduct = Alpine?.store("activeProduct")?.activeProduct;
  if (!activeProduct) return;

  const variants = activeProduct.variants || [];

  if (activeProduct.product_option_titles?.length > 1) {
    const store = ensureVMultiSelectStore();
    const selectedSize = store.activeOptionOne;

    refreshSizeAvailability(variants);

    if (selectedSize) {
      refreshStyleAvailabilityForSize(variants, selectedSize);
    } else {
      clearStyleRowNeutral();
    }

    refreshColorAvailabilityForSize(selectedSize);
  } else {
    refreshColorAvailabilityForSize(getSelectedSingleOptionSize(variants));
  }
};

const scheduleActiveProductAvailabilityRefresh = () => {
  Alpine.nextTick(() => {
    if (typeof window.requestAnimationFrame === "function") {
      window.requestAnimationFrame(refreshActiveProductAvailability);
      return;
    }

    refreshActiveProductAvailability();
  });
};

/**
 * Lazily ensure the vMultiSelect Alpine store exists.
 * The store is normally created in main.js, but on INF pages
 * the initial product may be single-option — so it won't exist
 * when the user scrolls to a multi-option product.
 */
const ensureVMultiSelectStore = () => {
  let store = Alpine.store("vMultiSelect");
  if (!store) {
    Alpine.store("vMultiSelect", {
      activeOptionOne: "",
      activeOptionTwo: "",
      updateActiveOptionOne(obj) {
        this.activeOptionOne = obj;
      },
      updateActiveOptionTwo(obj) {
        this.activeOptionTwo = obj;
      },
    });
    store = Alpine.store("vMultiSelect");
  }
  return store;
};

/**
 * For each size button (first .fusion-pdp__vselect-multi grid),
 * mark as unavailable if NO variant with that size is available (global check).
 */
const refreshSizeAvailability = (variants) => {
  const sizeGrid = document.querySelectorAll(".fusion-pdp__vselect-multi")?.[0];
  if (!sizeGrid) return;

  const options = sizeGrid.querySelectorAll(".fusion-pdp__vselect-option");
  options.forEach((option) => {
    const size = option.getAttribute("data-item");
    if (!size) return;

    const hasAvailable = variants.some(
      (v) => v.option1 === size && v.available,
    );

    if (hasAvailable) {
      option.removeAttribute("data-unavailable");
    } else {
      option.setAttribute("data-unavailable", "true");
    }
  });
};

/**
 * Remove all unavailable markers from the style row (second grid) — neutral state.
 * Used when no size is selected so styles don't show OOS prematurely.
 */
const clearStyleRowNeutral = () => {
  const styleGrid = document.querySelectorAll(".fusion-pdp__vselect-multi")?.[1];
  if (!styleGrid) return;

  const options = styleGrid.querySelectorAll(".fusion-pdp__vselect-option");
  options.forEach((option) => {
    option.removeAttribute("data-unavailable");
  });
};

/**
 * For each style button (second grid), mark as unavailable
 * if no variant matches selectedSize + style + available.
 * If no selectedSize provided, clears styles to neutral.
 */
const refreshStyleAvailabilityForSize = (variants, selectedSize) => {
  if (!selectedSize) {
    clearStyleRowNeutral();
    return;
  }

  const styleGrid = document.querySelectorAll(".fusion-pdp__vselect-multi")?.[1];
  if (!styleGrid) return;

  const options = styleGrid.querySelectorAll(".fusion-pdp__vselect-option");
  options.forEach((option) => {
    const style = option.getAttribute("data-item");
    if (!style) return;

    const hasAvailable = variants.some(
      (v) => v.option1 === selectedSize && v.option2 === style && v.available,
    );

    if (hasAvailable) {
      option.removeAttribute("data-unavailable");
    } else {
      option.setAttribute("data-unavailable", "true");
    }
  });
};

const clearColorOptionsNeutral = () => {
  document.querySelectorAll(".fusion-pdp__vcolor-option").forEach((option) => {
    option.removeAttribute("data-unavailable");
  });
};

const getProductForColorOption = (option) => {
  const productId = Number(option.id);
  if (!productId) return null;

  const dataProvider = window.fusionDataProvider;
  if (Array.isArray(dataProvider)) {
    const colorProduct = dataProvider.find(
      (item) => item?.product?.id === productId,
    )?.product;

    if (colorProduct) return colorProduct;
  }

  const activeProduct = Alpine?.store("activeProduct")?.activeProduct;
  if (activeProduct?.id === productId) return activeProduct;

  return null;
};

const getSelectedSingleOptionSize = (variants) => {
  const selectedVariantId = Alpine?.store("selectedVariant")?.id;
  if (!selectedVariantId) return "";

  const selectedVariant = variants.find(
    (variant) => variant.id === Number(selectedVariantId),
  );

  return selectedVariant?.option1 || "";
};

const refreshColorAvailabilityForSize = (selectedSize) => {
  if (!selectedSize) {
    clearColorOptionsNeutral();
    return;
  }

  document.querySelectorAll(".fusion-pdp__vcolor-option").forEach((option) => {
    const product = getProductForColorOption(option);
    if (!product?.variants?.length) {
      option.removeAttribute("data-unavailable");
      return;
    }

    const hasAvailable = product?.variants?.some(
      (variant) => variant.option1 === selectedSize && variant.available,
    );

    if (hasAvailable) {
      option.removeAttribute("data-unavailable");
    } else {
      option.setAttribute("data-unavailable", "true");
    }
  });
};

/**
 * Uses section rendering API to fetch and inject
 * an updated list of complementary product cards
 * @param {String} handle - New product handle
 */
const updateComplementaryProducts = (handle) => {
  const wrapper = document.getElementById("complementary-products-wrapper");

  if (!wrapper) {
    return;
  }

  wrapper.innerHTML = "";

  const request = new XMLHttpRequest();

  request.addEventListener("load", () => {
    if (request.status == 200) {
      wrapper.innerHTML = request.response;

      setFormEventListeners(wrapper);
    }
  });
  request.open(
    "GET",
    `/products/${handle}?section_id=complementary-products-pairs-with`,
    true,
  );
  request.send();
};
